import PropTypes from 'prop-types';
import {useState} from 'react';
import {createUseStyles, useTheme} from 'react-jss';
import {connect} from 'react-redux';
import ChangeButton from '../../components/ProgressSteps/ChangeButton/ChangeButton';
import BackButton from '../../components/ProgressSteps/ProgressButtons/BackButton';
import ForwardButton from '../../components/ProgressSteps/ProgressButtons/ForwardButton';
import Button from '../../UI/Button/Button';
import Surface from '../../UI/Surface/Surface';
import {showOverlay} from '../../v2/actions/overlay-actions';
import DownChevron from '../../v2/components/ui/svg/Components/DownChevron';
import Translation from '../../v2/lang/Translation';
import {isMobile} from '../../v2/selectors/DeviceSelectors';
import getHasLoginComponent from '../../v2/selectors/getHasLoginComponent';
import Spacing from '../../v2/style-helper/Spacing';
import SectionHeader from '../SectionHeader/SectionHeader';

const useStyles = createUseStyles({
  expandableButton: {
    display: 'flex',
  },
  chevron: {
    alignSelf: 'baseline',
    left: '5px',
    position: 'relative',
    transition: 'transform 0.4s',
    transform: props => props.expanded && 'rotate(-180deg)',
    top: '-3px',
  },
  sectionHeadLine: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: props => !props.isStepPending && Spacing.triple,
    flexDirection: props => (props.showGuestTitle ? 'column' : 'row'),
  },
  guestTitle: {
    fontFamily: props => props.theme.core.typography.secondary,
    fontWeight: 700,
    fontSize: 22,
  },
  guestSubTitle: {
    marginBottom: Spacing.triple,
  },
});

export function GenericSection(props) {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const classes = useStyles({...props, theme, expanded});
  const shouldOpenMobileModalWhenExpanded = props.isMobile && props.expandableModalId;
  const shouldOpenExpandedSection = !shouldOpenMobileModalWhenExpanded;
  
  const sectionStatus = () => {
    if (props.isStepPending) {
      return 'pending';
    }
    
    if (props.isStepCollapsed) {
      return 'collapsed';
    }
    
    if (props.isStepActive) {
      return 'active';
    }
  };
  
  const handleExpandableClick = () => {
    if (shouldOpenExpandedSection) {
      setExpanded(prevExpanded => !prevExpanded);
    } else {
      props.showOverlay(props.expandableModalId);
    }
  };
  
  return (
    <Surface active={props.isStepActive} noMarginTop={props.noMarginTop}>
      {props.expandable && !theme.sectionHeader.isHidden && (
        <div>
          <Button
            id="GenericSection__toggleExpandableSection"
            data-test="GenericSection__toggleExpandableSection"
            className={classes.expandableButton}
            ariaExpanded={expanded}
            onClick={handleExpandableClick}
            leftAlign
            link
            noResize
            noStyle
          >
            <SectionHeader
              step={props.step}
              id={props.id}
              headerKey={props.headerKey}
              isStepPending={props.isStepPending}
            />
    
            <span className={classes.chevron}>
              <DownChevron />
            </span>
          </Button>
  
          {shouldOpenExpandedSection && expanded && props.expandableContent}
        </div>
      )}
  
      {!props.expandable && !theme.sectionHeader.isHidden && (
        <div className={classes.sectionHeadLine} data-test="GenericSection" data-status={sectionStatus()}>
          {props.showGuestTitle && (
          <>
            <h2 className={classes.guestTitle}>
              <Translation>checkout.as.guest</Translation>
            </h2>
            <p className={classes.guestSubTitle}>
              <Translation>guest.checkout.sub.title</Translation>
            </p>
          </>
          )}
          
          <SectionHeader
            step={props.step}
            id={props.id}
            headerKey={props.headerKey}
            isStepPending={props.isStepPending}
          />
          
          {props.isStepCollapsed && <ChangeButton step={props.step} isPaymentSection={props.isPaymentSection} />}
        </div>
      )}
      
      {!props.isStepPending && (
        <>
          {props.children}
          {props.displayButtons && (
            <>
              <ForwardButton />
              <BackButton />
            </>
          )}
        </>
      )}
    </Surface>
  );
}

GenericSection.propTypes = ({
  expandable: PropTypes.bool,
  expandableContent: PropTypes.node,
  expandableModalId: PropTypes.string,
  noMarginTop: PropTypes.bool,
  step: PropTypes.number.isRequired,
  id: PropTypes.string.isRequired,
  headerKey: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  isMobile: PropTypes.bool,
  isStepActive: PropTypes.bool.isRequired,
  isStepPending: PropTypes.bool.isRequired,
  isStepCollapsed: PropTypes.bool.isRequired,
  displayButtons: PropTypes.bool.isRequired,
  isPaymentSection: PropTypes.bool,
  showGuestTitle: PropTypes.bool,
  showOverlay: PropTypes.func.isRequired,
});

const mapStateToProps = (state, ownProps) => ({
  displayButtons: ownProps.isStepActive && ownProps.step !== state.ui.sections.length,
  isMobile: isMobile(state),
  showGuestTitle: getHasLoginComponent(state) && ownProps.step === 1,
});

const mapDispatchToProps = dispatch => ({
  showOverlay(overlay) {
    dispatch(showOverlay(overlay));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(GenericSection);
